import React from 'react';
import styles from './ActionWrapper.module.scss';

/**
 * The click-target wrapper of the card (wraps the Image + card content).
 *
 * Port notes vs eb-ui `ActionWrapper.tsx`:
 * - `@eventbrite/http`'s `setWindowLocation(path)` → a direct
 *   `window.location.assign(path)` (that is all the helper did; the `http`
 *   package must not be imported in a Valkyrie port).
 * - The `onClick && !shouldOpenNewTab` branch rendered an EDS
 *   `<Button type="link" style="none" role="link">`. EDS Button with
 *   `type="link"` + `onClick` calls `preventDefault()` and ONLY runs the
 *   handler — navigation happened programmatically via `setWindowLocation`
 *   inside `handleClick` (the compensating call, see memory.md trap C). We
 *   render a real `<button role="link">` and keep the exact same behaviour:
 *   fire `onClick(id)`, then navigate to `path`.
 */
interface ActionWrapperProps {
    path: string;
    shouldOpenNewTab?: boolean;
    onClick?: (id: string | undefined) => void;
    id?: string;
    children?: React.ReactNode;
}

const handleClick = ({
    path,
    onClick,
    id,
}: {
    path?: string;
    onClick?: (id: string | undefined) => void;
    id?: string;
}) => {
    onClick?.(id);

    if (path) {
        window.location.assign(path);
    }
};

export const ActionWrapper: React.FunctionComponent<ActionWrapperProps> = (
    props,
) => {
    if (props.onClick && !props.shouldOpenNewTab) {
        return (
            <button
                type="button"
                role="link"
                className={styles.actionButton}
                onClick={() => {
                    handleClick({
                        path: props.path,
                        onClick: props.onClick,
                        id: props.id,
                    });
                }}
            >
                {props.children}
            </button>
        );
    }

    let aProps: Record<string, string | (() => void)> = { href: props.path };

    if (props.shouldOpenNewTab) {
        aProps = {
            ...aProps,
            target: '_blank',
            rel: 'noopener',
        };
    }

    if (props.onClick) {
        aProps = {
            ...aProps,
            onClick: () =>
                handleClick({ onClick: props.onClick, id: props.id }),
        };
    }

    return (
        <a className={styles.actionLink} {...aProps}>
            {props.children}
        </a>
    );
};
